import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Loader } from '@shared/ui/Progress';
import { FallbackBanner } from '@shared/ui/FallbackBanner';
import { IconCheck, IconChevronRight, IconPencil, IconTrash } from '@shared/ui/icons';
import { ContractVersionFiles } from '../ContractVersionFiles';
import { RiskAnnotation } from '../RiskAnnotation';
import { RiskRefs } from '../RiskRefs';
import {
  CATEGORY_LABELS,
  DocBadge,
  DocsGroup,
  DocsGroupTitle,
  DocsRow,
  DocsRowMeta,
  Empty,
  RiskDesc,
  Section,
  SEVERITY_LABELS,
  SeverityBadge,
  shortDocPath,
  VersionContent,
  VersionGroup,
  VersionHeader,
  VersionLabel,
  VersionMeta,
  VersionToggle,
} from './styles';

const HeaderTitle = styled.h4`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const ActionsCluster = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const ProtocolLoaderStatus = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  padding: 6px 10px;
  border-radius: 4px;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const RisksStats = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  display: flex;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const RiskItemCard = styled.div`
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const RiskItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ProposedChange = styled.div`
  margin-top: 6px;
  margin-left: 8px;
  padding-left: 10px;
  border-left: 2px solid ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SourceBadge = styled.span<{ $type: 'manual' | 'lawyer' }>`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  letter-spacing: 0.04em;
  background: ${({ theme, $type }) =>
    $type === 'manual' ? theme.colors.primary + '26' : theme.colors.status.warning + '26'};
  color: ${({ theme, $type }) =>
    $type === 'manual' ? theme.colors.primary : theme.colors.status.warning};
`;

const IconButton = styled.button<{ $danger?: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: ${({ theme, $danger }) => ($danger ? theme.colors.status.danger : theme.colors.text.muted)};
  display: flex;
`;

const DeviationCard = styled.div`
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const DeviationTemplateNote = styled.div`
  margin-top: 6px;
  margin-left: 8px;
  padding-left: 10px;
  border-left: 2px solid ${({ theme }) => theme.colors.status.success};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

import type { ContractRisk, ContractVersion } from '@entities/legal/types';

export interface ComposedProtocolItem {
  id: string;
  created_at: string;
  source_similarity: number | null;
}

interface ContractRisksSectionProps {
  contractId: string;
  contractStatus: string;
  versions: ContractVersion[];
  risks: ContractRisk[];
  templateDeviations?: Array<{
    clause: string;
    description: string;
    template_says?: string | null;
    severity?: string | null;
  }>;
  composedProtocols: ComposedProtocolItem[];
  can_write: boolean;
  collapsedVersions: Record<string, boolean>;
  onToggleVersion: (versionId: string) => void;
  selectMode: boolean;
  onEnterSelectMode: () => void;
  selectedRisks: Set<string>;
  onToggleRiskSelection: (id: string) => void;
  onOpenManualRisk: () => void;
  onOpenDispatchRisks: () => void;
  onComposeProtocol: () => void;
  isQueuingProtocol: boolean;
  isGeneratingProtocol: boolean;
  protocolFallback: string | null;
  onDismissProtocolFallback: () => void;
  onDownloadComposedProtocol: (id: string) => void;
  onDeleteComposedProtocol: (id: string) => void;
  onViewClause: (id: string) => void;
  onEditRisk: (id: string) => void;
  onDeleteRisk: (id: string) => void;
}

export function ContractRisksSection({
  contractId,
  contractStatus,
  versions,
  risks,
  templateDeviations,
  composedProtocols,
  can_write,
  collapsedVersions,
  onToggleVersion,
  selectMode,
  onEnterSelectMode,
  selectedRisks,
  onToggleRiskSelection,
  onOpenManualRisk,
  onOpenDispatchRisks,
  onComposeProtocol,
  isQueuingProtocol,
  isGeneratingProtocol,
  protocolFallback,
  onDismissProtocolFallback,
  onDownloadComposedProtocol,
  onDeleteComposedProtocol,
  onViewClause,
  onEditRisk,
  onDeleteRisk,
}: ContractRisksSectionProps) {
  const isReanalyzing = contractStatus === 'under_review';
  const versionsByNo = [...versions].sort((a, b) => b.version_no - a.version_no);
  const risksByVersion = new Map<string | null, ContractRisk[]>();
  for (const r of risks) {
    const key = r.version_id ?? null;
    const arr = risksByVersion.get(key) ?? [];
    arr.push(r);
    risksByVersion.set(key, arr);
  }
  const latestVersionId = versionsByNo[0]?.id ?? null;
  const isCollapsed = (id: string) => {
    if (id in collapsedVersions) return collapsedVersions[id];
    return id !== latestVersionId;
  };
  const totalRisks = risks.length;

  const accepted = risks.filter((r) => r.decision === 'accepted').length;
  const disputed = risks.filter((r) => r.decision === 'disputed').length;
  const rejected = risks.filter((r) => r.decision === 'rejected').length;
  const pending = totalRisks - accepted - disputed - rejected;

  return (
    <>
      <Section>
        <HeaderTitle>
          <span>Правовые риски ({totalRisks})</span>
          <ActionsCluster>
            {totalRisks > 0 && !selectMode && (
              <Button
                type="button"
                $variant="secondary"
                onClick={onEnterSelectMode}
                disabled={!can_write}
                title={!can_write ? 'Только чтение' : undefined}
              >
                Выбрать риски
              </Button>
            )}
            <Button
              type="button"
              $variant="secondary"
              onClick={onOpenManualRisk}
              disabled={!can_write}
              title={!can_write ? 'Только чтение' : undefined}
            >
              + Свой риск
            </Button>
            {totalRisks > 0 && (
              <Button
                type="button"
                onClick={onComposeProtocol}
                disabled={isQueuingProtocol || isGeneratingProtocol || !can_write}
                title={
                  !can_write
                    ? 'Только чтение'
                    : 'DOCX по шаблону «Шаблон протокола разногласий» из БЗ (фирменный бланк САТКОМ + таблица разногласий по критичным рискам)'
                }
              >
                {isQueuingProtocol || isGeneratingProtocol ? 'Составляем…' : 'Составить протокол разногласий'}
              </Button>
            )}
            {totalRisks > 0 && (
              <Button
                type="button"
                $variant="secondary"
                onClick={onOpenDispatchRisks}
                disabled={!can_write}
                title={
                  !can_write
                    ? 'Только чтение'
                    : 'Сформировать по отделам и разослать: письмо на ящик отдела + уведомление сотрудникам в приложении'
                }
              >
                Разослать риски по отделам
              </Button>
            )}
          </ActionsCluster>
        </HeaderTitle>

        {isGeneratingProtocol && (
          <ProtocolLoaderStatus role="status">
            <Loader />
            Составляем протокол разногласий (обычно занимает 30-60 сек)...
          </ProtocolLoaderStatus>
        )}

        {protocolFallback && (
          <div style={{ marginBottom: 12 }}>
            <FallbackBanner title="Не удалось собрать DOCX-протокол." onDismiss={onDismissProtocolFallback}>
              {protocolFallback}
            </FallbackBanner>
          </div>
        )}

        {composedProtocols.length > 0 && (
          <DocsGroup>
            <DocsGroupTitle>Протоколы разногласий DOCX ({composedProtocols.length})</DocsGroupTitle>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {composedProtocols.map((it) => (
                <DocsRow key={it.id}>
                  <DocsRowMeta>
                    <Link to={`/agents/statut/documents/${it.id}`}>
                      {new Date(it.created_at).toLocaleString('ru-RU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Link>
                    {/* У протокола сходство с шаблоном всегда около нуля — «похожесть 0%»
                      только путала. Показываем, когда оно что-то значит. */}
                    {it.source_similarity !== null && it.source_similarity >= 0.05 && (
                      <span style={{ marginLeft: 8 }}>· похожесть {(it.source_similarity * 100).toFixed(0)}%</span>
                    )}
                  </DocsRowMeta>
                  <Button type="button" $size="sm" $variant="secondary" onClick={() => onDownloadComposedProtocol(it.id)}>
                    Скачать
                  </Button>
                  <Button
                    type="button"
                    $size="sm"
                    $variant="ghost"
                    onClick={() => onDeleteComposedProtocol(it.id)}
                    disabled={!can_write}
                    title={!can_write ? 'Только чтение' : 'Удалить этот сгенерированный протокол'}
                  >
                    Удалить
                  </Button>
                </DocsRow>
              ))}
            </ul>
          </DocsGroup>
        )}

        {totalRisks > 0 && (
          <RisksStats>
            <span>
              На разметке: <strong>{pending}</strong>
            </span>
            <span style={{ color: 'var(--osnova-success, #4caf50)' }}>
              ✓ Принято: <strong>{accepted}</strong>
            </span>
            <span style={{ color: 'var(--osnova-warning, #ff9800)' }}>
              ? Под вопросом: <strong>{disputed}</strong>
            </span>
            <span style={{ color: 'var(--osnova-danger, #d04848)' }}>
              ✗ Отклонено: <strong>{rejected}</strong>
            </span>
            <span style={{ flex: 1, textAlign: 'right' }}>
              В протокол попадут <strong>{totalRisks - rejected - disputed}</strong> пунктов
            </span>
          </RisksStats>
        )}

        {totalRisks === 0 && !isReanalyzing ? (
          <Empty>Анализа ещё не было. Загрузите файл договора, чтобы получить экспертизу.</Empty>
        ) : versionsByNo.length === 0 ? null : (
          versionsByNo.map((v) => {
            const vRisks = risksByVersion.get(v.id) ?? [];
            const collapsed = isCollapsed(v.id);
            const isLatest = v.id === latestVersionId;
            return (
              <VersionGroup key={v.id}>
                <VersionHeader type="button" onClick={() => onToggleVersion(v.id)} aria-expanded={!collapsed}>
                  <VersionToggle $open={!collapsed}>
                    {collapsed ? <IconChevronRight size={18} /> : <IconCheck size={18} />}
                  </VersionToggle>
                  <VersionLabel>
                    Версия v{v.version_no}
                    {isLatest && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--osnova-primary, #5a8dee)' }}>· последняя</span>}
                  </VersionLabel>
                  <VersionMeta>
                    {vRisks.length === 0
                      ? isReanalyzing
                        ? 'идёт анализ…'
                        : 'без рисков'
                      : `${vRisks.length} ${vRisks.length === 1 ? 'риск' : 'риск(ов)'}`}
                    {' · '}
                    {v.source === 'counterparty' ? 'от контрагента' : 'наша'}
                  </VersionMeta>
                </VersionHeader>
                {!collapsed && (
                  <VersionContent>
                    <ContractVersionFiles contractId={contractId} versionId={v.id} versionNo={v.version_no} />
                    {vRisks.length === 0 ? (
                      <Empty style={{ paddingTop: 8 }}>По этой версии рисков не зафиксировано.</Empty>
                    ) : (
                      vRisks.map((r) => (
                        <RiskItemCard key={r.id}>
                          <RiskItemHeader>
                            {selectMode && (
                              <input
                                type="checkbox"
                                checked={selectedRisks.has(r.id)}
                                onChange={() => onToggleRiskSelection(r.id)}
                                style={{ width: 18, height: 18, cursor: 'pointer', flexShrink: 0 }}
                                aria-label={`Выбрать риск ${r.description.slice(0, 40)}`}
                              />
                            )}
                            <SeverityBadge $sev={r.severity}>
                              {SEVERITY_LABELS[r.severity] ?? r.severity}
                            </SeverityBadge>
                            <RiskDesc
                              role="button"
                              tabIndex={0}
                              onClick={() => (selectMode ? onToggleRiskSelection(r.id) : onViewClause(r.id))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  if (selectMode) onToggleRiskSelection(r.id);
                                  else onViewClause(r.id);
                                }
                              }}
                              title={
                                selectMode
                                  ? 'Кликни, чтобы добавить/убрать из выборки'
                                  : r.clause_quote
                                    ? 'Кликни, чтобы увидеть исходный текст пункта'
                                    : 'Кликни, чтобы увидеть подробности риска'
                              }
                              style={{ cursor: 'pointer' }}
                            >
                              <strong>{CATEGORY_LABELS[r.category] ?? r.category}.</strong> {r.description}
                            </RiskDesc>
                            {r.source_document && (
                              <DocBadge title={r.source_document}>{shortDocPath(r.source_document)}</DocBadge>
                            )}
                            {r.source === 'manual' && <SourceBadge $type="manual">ДОБАВЛЕН ЮРИСТОМ</SourceBadge>}
                            {r.source === 'lawyer_corrected' && (
                              <SourceBadge $type="lawyer">ПРАВЛЕН ЮРИСТОМ</SourceBadge>
                            )}
                            {r.confidence != null && (
                              <span style={{ fontSize: 11, color: '#888' }}>{(r.confidence * 100).toFixed(0)}%</span>
                            )}
                            <IconButton type="button" title="Редактировать" onClick={() => onEditRisk(r.id)}>
                              <IconPencil size={14} />
                            </IconButton>
                            <IconButton type="button" title="Удалить риск" $danger onClick={() => onDeleteRisk(r.id)}>
                              <IconTrash size={14} />
                            </IconButton>
                          </RiskItemHeader>
                          {r.proposed_change && (
                            <ProposedChange>
                              <strong>В редакции АО «САТКОМ»:</strong> {r.proposed_change}
                            </ProposedChange>
                          )}
                          <RiskRefs refs={r.refs} />
                          <RiskAnnotation risk={r} contractId={contractId} />
                        </RiskItemCard>
                      ))
                    )}
                  </VersionContent>
                )}
              </VersionGroup>
            );
          })
        )}
      </Section>

      {templateDeviations && templateDeviations.length > 0 && (
        <Section>
          <h4>Отклонения от типовой формы САТКОМ ({templateDeviations.length})</h4>
          <Empty style={{ fontStyle: 'normal', marginBottom: 12 }}>
            LLM сверила договор с корпоративным стандартом и нашла эти расхождения. Это отдельный блок — пункт может
            одновременно быть и риском, и отклонением.
          </Empty>
          {templateDeviations.map((d, idx) => (
            <DeviationCard key={`${d.clause}-${idx}`}>
              <RiskItemHeader>
                {d.severity && (
                  <SeverityBadge $sev={d.severity}>{SEVERITY_LABELS[d.severity] ?? d.severity}</SeverityBadge>
                )}
                <RiskDesc>
                  <strong>Пункт {d.clause}.</strong> {d.description}
                </RiskDesc>
              </RiskItemHeader>
              {d.template_says && (
                <DeviationTemplateNote>
                  <strong>По типовой форме САТКОМ:</strong> {d.template_says}
                </DeviationTemplateNote>
              )}
            </DeviationCard>
          ))}
        </Section>
      )}
    </>
  );
}
